// Pairing and unpairing from the app (AD-22, AD-43, FR-6, FR-12), framework-free like the rest of the core (AD-34).
// Talks to the REST part of the app contract; every answer is checked at runtime.
import {
  normalizePairingCode,
  parseApiError,
  parsePairingConfirmResponse,
  parsePairingLookupResponse,
  validate,
  type ApiError,
  type Parsed,
  type PairingConfirmRequest,
  type PairingConfirmResponse,
  type PairingLookupRequest,
  type PairingLookupResponse,
} from '@brewcore/app-contract';
import { reasonText, t, type Lang } from './i18n.js';

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: ApiError };

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/** Token of a QR link https://app.<domain>/p#<token>: only from the URL fragment (AD-42), never from path or query. */
export function pairingTokenFromLocation(loc: { pathname: string; hash: string }): string | null {
  if (!/^\/p\/?$/.test(loc.pathname)) return null;
  const token = loc.hash.replace(/^#/, '');
  return /^[A-Za-z0-9_-]{22,40}$/.test(token) ? token : null;
}

export class PairingApi {
  constructor(
    private readonly fetchImpl: FetchLike = (input, init) => fetch(input, init),
    private readonly base = '',
  ) {}

  lookup(input: PairingLookupRequest): Promise<ApiResult<PairingLookupResponse>> {
    return this.call('/api/pairing/lookup', input, parsePairingLookupResponse);
  }

  confirm(input: PairingConfirmRequest): Promise<ApiResult<PairingConfirmResponse>> {
    return this.call('/api/pairing/confirm', input, parsePairingConfirmResponse);
  }

  unpair(machineId: string): Promise<ApiResult<{ machineId: string }>> {
    return this.call(`/api/machines/${encodeURIComponent(machineId)}/unpair`, {}, (raw) =>
      validate('http-unpair-response', raw).ok
        ? { ok: true, value: { machineId } }
        : { ok: false, error: 'malformed' },
    );
  }

  private async call<T>(
    path: string,
    body: unknown,
    parse: (raw: unknown) => Parsed<T>,
  ): Promise<ApiResult<T>> {
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.base}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      return { ok: false, error: { code: 'offline' } };
    }
    let raw: unknown = null;
    try {
      raw = await res.json();
    } catch {
      // no JSON body
    }
    if (!res.ok) return { ok: false, error: parseApiError(raw) };
    const parsed = parse(raw);
    return parsed.ok ? { ok: true, value: parsed.value } : { ok: false, error: { code: 'malformed' } };
  }
}

/** The code as typed, checked before it goes to the API (8 characters, Crockford look-alikes allowed). */
export function checkTypedCode(input: string): string | null {
  return normalizePairingCode(input);
}

/** Text for a failed pairing or unpairing; "limit" names the wait. */
export function pairingErrorText(lang: Lang, error: ApiError): string {
  if (error.code === 'limit' && error.retryAfter)
    return t(lang, 'pairTooMany', { min: Math.max(1, Math.ceil(error.retryAfter / 60)) });
  if (error.code === 'unauthorized') return t(lang, 'notLoggedIn');
  if (error.code === 'offline') return t(lang, 'net_cloud_unreachable');
  return reasonText(lang, error.code);
}

/** Name suggestion for a machine type ("orione" -> "Orione"). */
export function defaultMachineName(type: string): string {
  const s = type.replace(/[^a-z0-9]/gi, ' ').trim();
  return s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}`.slice(0, 40) : 'Maschine';
}
